import { useEffect, useState } from "react";
import axios from "axios";
import "./Modal.scss";
import "./VerifyBookingModal.scss";

const BACKEND = process.env.REACT_APP_BACKEND;

const egp = (cents) => `${(Number(cents || 0) / 100).toLocaleString(undefined, { minimumFractionDigits: 2 })} EGP`;

const VERIFICATION_LABELS = {
  awaiting: "Awaiting ID verification",
  approved: "IDs approved",
  declined: "IDs declined",
};

const REFUND_LABELS = {
  requested: "Refund in progress",
  succeeded: "Refunded",
  failed: "Refund failed",
};

// Lowercase, hyphenated, filesystem-safe; keeps Arabic and other letters
const slug = (text) =>
  String(text || "").trim().toLowerCase()
    .replace(/[^\p{L}\p{N}]+/gu, "-")
    .replace(/^-+|-+$/g, "");

// name-boat-date-side.jpg, e.g. ahmed-ali-sea-breeze-2026-10-20-front.jpg
const idFileName = (booking, guest, index, side) => {
  const name = slug(guest.name) || `passenger-${index + 1}`;
  return [name, slug(booking.boatName), slug(booking.date), side].filter(Boolean).join("-") + ".jpg";
};

// Stored images are webp; re-encode to JPEG in the browser before saving
const toJpegBlob = (url) => new Promise((resolve, reject) => {
  const img = new Image();
  img.onload = () => {
    const canvas = document.createElement("canvas");
    canvas.width = img.naturalWidth;
    canvas.height = img.naturalHeight;
    const ctx = canvas.getContext("2d");
    ctx.fillStyle = "#fff";
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(img, 0, 0);
    canvas.toBlob((blob) => (blob ? resolve(blob) : reject(new Error("JPEG encode failed"))), "image/jpeg", 0.92);
  };
  img.onerror = reject;
  img.src = url;
});

const saveAsJpeg = async (url, filename) => {
  const jpegUrl = URL.createObjectURL(await toJpegBlob(url));
  const a = document.createElement("a");
  a.href = jpegUrl;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(jpegUrl), 1000);
};

// ID images are admin-only, so they are fetched with the auth header and
// shown from object URLs rather than linked directly.
function useIdImages(booking) {
  const [urls, setUrls] = useState({});
  const [error, setError] = useState("");

  useEffect(() => {
    if (!booking?.guestIds?.length) return undefined;
    let cancelled = false;
    const created = [];
    const files = booking.guestIds.flatMap((g) => [g.front, g.back]);

    Promise.all(files.map(async (file) => {
      const res = await axios.get(`${BACKEND}/api/booking/${booking._id}/ids/${file}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      created.push(url);
      return [file, url];
    }))
      .then((pairs) => { if (!cancelled) setUrls(Object.fromEntries(pairs)); })
      .catch(() => { if (!cancelled) setError("Could not load the ID images."); });

    return () => {
      cancelled = true;
      created.forEach((u) => URL.revokeObjectURL(u));
    };
  }, [booking?._id, booking?.guestIds]);

  return { urls, error };
}

function VerifyBookingModal({ booking, onClose, onUpdated, notifySuccess, notifyFailure, slotText }) {
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [enlarged, setEnlarged] = useState(null);
  const { urls, error } = useIdImages(booking);

  useEffect(() => {
    if (!booking) return undefined;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = ""; };
  }, [booking]);

  if (!booking) return null;

  const download = async (url, filename) => {
    try {
      await saveAsJpeg(url, filename);
    } catch {
      notifyFailure("Could not download the ID image");
    }
  };

  const downloadAll = async () => {
    for (const [i, g] of booking.guestIds.entries()) {
      for (const side of ["front", "back"]) {
        if (urls[g[side]]) await download(urls[g[side]], idFileName(booking, g, i, side));
      }
    }
  };

  const imagesReady = booking.guestIds?.length > 0 && Object.keys(urls).length > 0;

  const status = booking.verification?.status;
  const refund = booking.refund || {};
  const payment = booking.payment || {};

  const decide = async (decision) => {
    if (decision === "decline") {
      const ok = window.confirm(
        `Decline this booking and refund ${egp(payment.refundableCents)} to the customer?\n` +
        `(Deposit ${egp(payment.amountCents)} minus Paymob fee ${egp(payment.feeCents)})`
      );
      if (!ok) return;
    }
    setBusy(true);
    try {
      const res = await axios.patch(`${BACKEND}/api/booking/${booking._id}/verify`, { decision, note });
      onUpdated(res.data.booking);
      if (decision === "approve") {
        notifySuccess("Booking confirmed, the customer has been emailed");
      } else if (res.data.booking.refund?.status === "succeeded") {
        notifySuccess("Booking declined and deposit refunded");
      } else {
        notifyFailure("Booking declined, but the refund failed. Use Retry refund.");
      }
    } catch (err) {
      notifyFailure(err.response?.data?.message || "Failed to update booking");
    } finally {
      setBusy(false);
    }
  };

  const retryRefund = async () => {
    setBusy(true);
    try {
      const res = await axios.post(`${BACKEND}/api/booking/${booking._id}/refund-retry`);
      onUpdated(res.data.booking);
      if (res.data.booking.refund?.status === "succeeded") notifySuccess("Deposit refunded");
      else notifyFailure("Refund failed again: " + (res.data.booking.refund?.error || "unknown error"));
    } catch (err) {
      notifyFailure(err.response?.data?.message || "Failed to retry refund");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="popup-overlay-admin"
      onClick={(e) => e.target.classList.contains("popup-overlay-admin") && !busy && onClose()}
    >
      <div className="popup-content large verify-modal">
        <button className="close-button" onClick={onClose} disabled={busy} aria-label="Close">
          &times;
        </button>
        <h1>ID Verification</h1>

        <div className="verify-status-row">
          <span className={`verify-pill ${status}`}>{VERIFICATION_LABELS[status] || status}</span>
          {refund.status && refund.status !== "none" && (
            <span className={`verify-pill refund-${refund.status}`}>{REFUND_LABELS[refund.status]}</span>
          )}
        </div>

        <div className="verify-details">
          <div><span>Name</span><strong>{booking.bookedBy}</strong></div>
          <div><span>Email</span><strong>{booking.email}</strong></div>
          <div><span>Phone</span><strong>{booking.number}</strong></div>
          <div><span>Boat</span><strong>{booking.boatName}</strong></div>
          <div><span>Date</span><strong>{booking.date}</strong></div>
          <div><span>Time</span><strong>{booking.timeText || slotText(booking.slot1, booking.slot2, booking.slot3)}</strong></div>
          <div><span>Destination</span><strong>{booking.destination}</strong></div>
          <div><span>Deposit paid</span><strong>{egp(payment.amountCents)}</strong></div>
          <div><span>Refund if declined</span><strong>{egp(payment.refundableCents)}</strong></div>
        </div>

        <div className="verify-subheading-row">
          <h2 className="verify-subheading">
            Passengers from outside El Gouna ({booking.guestCount || booking.guestIds?.length || 0})
          </h2>
          {!booking.idsPurgedAt && imagesReady && (
            <button type="button" className="verify-download-all" onClick={downloadAll}>
              <i className="fa-solid fa-download" aria-hidden="true"></i>
              Download all
            </button>
          )}
        </div>

        {booking.idsPurgedAt ? (
          <p className="verify-empty">ID images were deleted on {new Date(booking.idsPurgedAt).toLocaleDateString()}.</p>
        ) : !booking.guestIds?.length ? (
          <p className="verify-empty">No ID images were attached to this booking. Contact the customer before deciding.</p>
        ) : (
          <>
            {error && <p className="verify-empty">{error}</p>}
            <div className="verify-passengers">
              {booking.guestIds.map((g, i) => (
                <div className="verify-passenger" key={g.front}>
                  <p>Passenger {i + 1}{g.name ? `: ${g.name}` : ""}</p>
                  <div className="verify-id-pair">
                    {["front", "back"].map((side) => (
                      <div className="verify-id-cell" key={side}>
                        <button
                          type="button"
                          className="verify-id-thumb"
                          onClick={() => urls[g[side]] && setEnlarged(urls[g[side]])}
                          aria-label={`Enlarge passenger ${i + 1} ID ${side}`}
                        >
                          {urls[g[side]] ? <img src={urls[g[side]]} alt={`Passenger ${i + 1} ID ${side}`} /> : <span>Loading...</span>}
                          <small>{side}</small>
                        </button>
                        {urls[g[side]] && (
                          <button
                            type="button"
                            className="verify-id-download"
                            onClick={() => download(urls[g[side]], idFileName(booking, g, i, side))}
                            aria-label={`Download passenger ${i + 1} ID ${side}`}
                            title={idFileName(booking, g, i, side)}
                          >
                            <i className="fa-solid fa-download" aria-hidden="true"></i>
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </>
        )}

        {booking.verification?.decidedAt && (
          <p className="verify-decided">
            Decided by {booking.verification.decidedByName} on {new Date(booking.verification.decidedAt).toLocaleString()}
            {booking.verification.note ? `: ${booking.verification.note}` : ""}
          </p>
        )}
        {refund.status === "failed" && refund.error && (
          <p className="verify-refund-error">Paymob error: {refund.error}</p>
        )}

        {status === "awaiting" && (
          <label className="verify-note">
            Internal note (optional)
            <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2}
              placeholder="e.g. approved by gate security, ref 1234" />
          </label>
        )}

        <div className="modal-footer">
          <button className="cancel-btn" onClick={onClose} disabled={busy}>Close</button>
          {status === "awaiting" && (
            <>
              <button className="decline-btn" onClick={() => decide("decline")} disabled={busy}>
                Decline and refund
              </button>
              <button className="save-btn" onClick={() => decide("approve")} disabled={busy}>
                Confirm booking
              </button>
            </>
          )}
          {status === "declined" && refund.status === "failed" && (
            <button className="save-btn" onClick={retryRefund} disabled={busy}>Retry refund</button>
          )}
        </div>
      </div>

      {enlarged && (
        <div className="verify-lightbox" onClick={() => setEnlarged(null)}>
          <img src={enlarged} alt="ID enlarged" />
        </div>
      )}
    </div>
  );
}

export default VerifyBookingModal;
